import React, { useEffect, useState } from "react";
import { Alert, Spinner, Modal, Button } from "react-bootstrap";
import PrettyContainer from "../../prettyContainer/PrettyContainer";
import { getAppStatus, moverClientesApp, setAppStatusAuto } from "../../../helpers/api";

const MUTED = "#6b7a90";
const COLORES = { ok: "#1f9d55", caida: "#d64545", "sin-dato": "#9aa5b4" };
const TEXTO_ESTADO = { ok: "Funcionando", caida: "Caída", "sin-dato": "Sin dato" };
const NOMBRES = { 11: "ALLDATA V2", 2: "Identifix", 6: "ProDemand" };

const fecha = (d) => (d ? new Date(d).toLocaleString() : "—");
const hace = (d) => {
  if (!d) return "";
  const m = Math.round((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 1) return "hace un momento";
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  return h < 48 ? `hace ${h} h ${m % 60} min` : `hace ${Math.floor(h / 24)} días`;
};

// Panel del vigilante de apps (backend/middleware/Monitor.js). Muestra si
// ALLDATA V2, Identifix y ProDemand funcionan, y deja prestar otra app del
// grupo a los clientes de la que se cayo. Nada cambia para el cliente si no se
// presiona un boton (o se enciende el modo automatico).
export default function AppStatusPage() {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState("");
  const [confirmar, setConfirmar] = useState(null); // { tipo, app?, valor }
  const [guardando, setGuardando] = useState(false);

  const cargar = () =>
    getAppStatus()
      .then((d) => { setDatos(d); setError(""); })
      .catch((m) => setError(String(m)));

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 30000);
    return () => clearInterval(t);
  }, []);

  const aplicar = () => {
    if (!confirmar) return;
    setGuardando(true);
    const p =
      confirmar.tipo === "auto"
        ? setAppStatusAuto(confirmar.valor)
        : moverClientesApp(confirmar.app, confirmar.valor);
    p.then(cargar)
      .catch((m) => setError(String(m)))
      .finally(() => { setGuardando(false); setConfirmar(null); });
  };

  const otras = (app) => [11, 2, 6].filter((x) => x !== app).map((x) => NOMBRES[x]).join(" / ");

  const describirEvento = (e) => {
    const n = NOMBRES[e.app] || "";
    switch (e.tipo) {
      case "caida": return `${n} se cayó${e.detalle ? ` (${e.detalle})` : ""}`;
      case "recuperada": return `${n} volvió${e.minutos != null ? ` — estuvo caída ${e.minutos} min` : ""}`;
      case "mover": return `Se movieron los clientes de ${n}${e.por ? ` (por ${e.por})` : ""}`;
      case "regresar": return `Se regresaron los clientes de ${n}${e.por ? ` (por ${e.por})` : ""}`;
      case "auto": return `Modo automático ${e.detalle || ""}${e.por ? ` (por ${e.por})` : ""}`;
      default: return e.tipo;
    }
  };

  return (
    <PrettyContainer tittle="APP STATUS">
      <div style={{ paddingTop: "0.6em", maxWidth: "860px" }}>
        <p style={{ color: MUTED }}>
          Se revisa cada minuto si ALLDATA V2, Identifix y ProDemand funcionan. Si una se cae,
          con <b>Mover clientes</b> a quienes la tienen se les presta otra que funcione y que
          no tengan, así conservan la misma cantidad de programas (mitad y mitad). Con <b>Regresar clientes</b> todo vuelve a como estaba. Si no
          presionas nada, a los clientes no se les cambia nada.
        </p>

        {error && <Alert variant="warning">{error}</Alert>}

        {!datos ? (
          <div style={{ textAlign: "center", padding: "20px", color: MUTED }}>
            <Spinner animation="border" size="sm" /> Cargando…
          </div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "14px" }}>
              {datos.apps.map((a) => {
                const movida = datos.movidas.includes(a.app);
                const manual = (datos.manuales || []).includes(a.app);
                return (
                  <div key={a.app} style={{ background: "#fff", border: "1px solid #e6ebf2", borderRadius: "14px", padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ width: 12, height: 12, borderRadius: "50%", background: COLORES[a.estado] || COLORES["sin-dato"], flex: "none" }} />
                      <b style={{ fontSize: "1.05rem", color: "#1d2735" }}>{a.nombre}</b>
                    </div>
                    <div style={{ marginTop: "6px", fontWeight: 700, color: COLORES[a.estado] || MUTED }}>
                      {TEXTO_ESTADO[a.estado] || a.estado}
                      {a.desde && a.estado !== "sin-dato" ? (
                        <span style={{ fontWeight: 400, color: MUTED }}> · {hace(a.desde)}</span>
                      ) : null}
                    </div>
                    {a.detalle && <div style={{ fontSize: "0.82rem", color: MUTED, marginTop: "2px" }}>{a.detalle}</div>}
                    <div style={{ fontSize: "0.78rem", color: MUTED, marginTop: "4px" }}>
                      Última revisión: {a.ultimaRevision ? hace(a.ultimaRevision) : "todavía no"}
                    </div>
                    <div style={{ fontSize: "0.85rem", color: "#344054", marginTop: "8px" }}>
                      {datos.afectados[a.app] || 0} clientes recibirían otra si cae
                    </div>

                    {movida && (
                      <div style={{ marginTop: "8px", fontSize: "0.85rem", color: "#b7791f", fontWeight: 700 }}>
                        Clientes movidos a {otras(a.app)}
                        {!manual && datos.auto ? " (automático)" : ""}
                      </div>
                    )}

                    <div style={{ marginTop: "10px" }}>
                      {manual ? (
                        <Button size="sm" variant="success" onClick={() => setConfirmar({ tipo: "mover", app: a.app, valor: false })}>
                          Regresar clientes
                        </Button>
                      ) : (
                        <Button size="sm" variant={a.estado === "caida" ? "danger" : "outline-secondary"} onClick={() => setConfirmar({ tipo: "mover", app: a.app, valor: true })}>
                          Mover clientes
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: "18px", background: "#fff", border: "1px solid #e6ebf2", borderRadius: "14px", padding: "14px 16px", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 260px" }}>
                <b>Modo automático: {datos.auto ? "ENCENDIDO" : "apagado"}</b>
                <div style={{ fontSize: "0.85rem", color: MUTED }}>
                  Encendido, el vigilante mueve y regresa a los clientes solo cuando detecta una caída
                  (3 fallas seguidas) y cuando vuelve (5 minutos estable).
                </div>
              </div>
              <Button size="sm" variant={datos.auto ? "outline-danger" : "outline-primary"} onClick={() => setConfirmar({ tipo: "auto", valor: !datos.auto })}>
                {datos.auto ? "Apagar automático" : "Encender automático"}
              </Button>
            </div>

            <h6 style={{ marginTop: "22px", fontWeight: 800, color: "#1d2735" }}>Historial</h6>
            {datos.historial.length === 0 ? (
              <div style={{ color: MUTED, fontSize: "0.9rem" }}>Todavía no hay eventos.</div>
            ) : (
              <div style={{ background: "#fff", border: "1px solid #e6ebf2", borderRadius: "12px", overflow: "hidden" }}>
                {datos.historial.map((e) => (
                  <div key={e._id} style={{ display: "flex", gap: "12px", padding: "8px 12px", borderBottom: "1px solid #f0f3f7", fontSize: "0.88rem", flexWrap: "wrap" }}>
                    <span style={{ color: MUTED, whiteSpace: "nowrap", minWidth: "150px" }}>{fecha(e.fecha)}</span>
                    <span style={{ color: e.tipo === "caida" ? "#d64545" : e.tipo === "recuperada" ? "#1f9d55" : "#344054" }}>
                      {describirEvento(e)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Modal show={!!confirmar} onHide={() => !guardando && setConfirmar(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.05rem" }}>¿Confirmar?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmar?.tipo === "auto"
            ? confirmar.valor
              ? "El vigilante moverá y regresará clientes solo, sin que presiones nada."
              : "Se apaga el modo automático. Lo que esté movido a mano se queda como está."
            : confirmar?.valor
            ? `Los ${datos?.afectados?.[confirmar.app] || 0} clientes que tienen ${NOMBRES[confirmar?.app]} recibirán temporalmente otra que no tengan (${otras(confirmar?.app)}; mitad y mitad, solo las que funcionen).`
            : `Se quita la app prestada a los clientes de ${NOMBRES[confirmar?.app]}; vuelven a tener solo la suya.`}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="link" style={{ color: "#888" }} onClick={() => setConfirmar(null)} disabled={guardando}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={aplicar} disabled={guardando}>
            {guardando ? <Spinner animation="border" size="sm" /> : "Confirmar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </PrettyContainer>
  );
}
